import clsx from 'clsx'
import { type Ref, computed, defineComponent, onMounted, ref, useId, watch } from 'vue'

interface Props {
  tData?: string
  mainRef?: Ref<HTMLElement | null>
  class?: string
  inputClass?: string
  name?: string
  placeholder?: string
  value: string | number
  maxlength?: number
  min?: number
  max?: number
  disabled?: boolean
  autofocus?: boolean
  error?: string | boolean
  description?: string
  autocomplete?: string
  required?: boolean
  readonly?: boolean
  isSelecteMode?: boolean
  inputId?: string
  label?: string
  variant?: 'floating'
  onFocus?: (payload: FocusEvent) => void
  onBlur?: (payload: FocusEvent) => void
  onInput?: (value: string, payload: Event) => void
  onKeypress?: (payload: KeyboardEvent) => void
  onKeydown?: (payload: KeyboardEvent) => void
}

export default defineComponent((props: Props) => {
  const componentId = useId()
  const inputElm = ref<HTMLInputElement | null>(null)
  const localErrorMessage = ref<string>()

  // #region Props Getters

  const tData = computed(() => props.tData ?? 'v-textarea')
  const inputId = computed((): string => props.inputId ?? componentId)

  // #endregion

  // #region Getters

  const isFloating = computed((): boolean => props.variant === 'floating')

  const errorMessage = computed((): string | undefined => {
    if (props.error === true && localErrorMessage.value !== undefined) {
      return localErrorMessage.value
    }

    if (typeof props.error === 'string' && props.error.trim() !== '') {
      return props.error
    }

    return undefined
  })

  const isShowDescription = computed((): boolean => errorMessage.value === undefined && (typeof props.description === 'string' && props.description !== ''))

  const hasError = computed((): boolean => props.error === true || errorMessage.value !== undefined)

  const placeholder = computed((): string | undefined => {
    if (isFloating.value && props.placeholder === undefined) {
      return props.label
    }

    return props.placeholder
  })

  const hasPlaceholder = computed((): boolean => props.placeholder !== undefined)

  // #endregion

  // #region Actions

  const handleInvalid = (): void => {
    localErrorMessage.value = inputElm.value?.validationMessage
    inputElm.value?.setCustomValidity('')
  }

  // #endregion

  // #region Watchers

  if (props.isSelecteMode) {
    watch(() => props.value, () => {
      localErrorMessage.value = undefined
    })
  }

  watch(() => props.disabled, () => {
    localErrorMessage.value = undefined
  })

  // #endregion

  onMounted(() => {
    if (props.autofocus) {
      window.setTimeout(() => inputElm.value?.focus(), 0)
    }
  })

  return () => (
    <div
      t-data={tData.value}
      class={['relative', props.class]}
    >
      {!isFloating.value && props.label &&
        <label
          for={inputId.value}
          class={clsx(
            'mb-1 text-subtitle-2',
            {
              'text-neutral': !props.disabled,
              'text-neutral-400': props.disabled,
              'after:text-subtitle-2 after:text-danger-600 after:content-["*"]': props.required
            }
          )}
        >{props.label}
        </label>}
      <textarea
        ref={(ref) => {
          inputElm.value = ref as HTMLInputElement

          if (props.mainRef?.value !== undefined) {
            props.mainRef.value = ref as HTMLInputElement
          }
        }}
        id={inputId.value}
        class={clsx([
          'peer textarea',
          hasError.value ? 'textarea-error' : '',
          props.inputClass
        ], {
          'placeholder:text-transparent disabled:placeholder:text-transparent': isFloating.value && !hasPlaceholder.value
        })}
        name={props.name}
        placeholder={placeholder.value}
        value={props.value}
        maxlength={props.maxlength}
        disabled={props.disabled}
        autocomplete={props.autocomplete}
        required={props.required}
        readonly={props.readonly}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        onInput={(e) => {
          const value = (e.target as HTMLInputElement).value
          localErrorMessage.value = undefined
          props.onInput?.(value, e)
        }}
        onKeypress={props.onKeypress}
        onInvalid={handleInvalid}
        onKeydown={props.onKeydown}
      />
      {isFloating.value &&
        <label
          for={inputId.value}
          class={clsx(
            'absolute -top-1.5 left-3 z-0 text-input-label transition-all',
            'before:absolute before:left-0 before:top-1/2 before:-z-10 before:h-1.5 before:w-full before:translate-y-[-1.5px] before:bg-white peer-focus:before:translate-y-[-3px]',
            {
              'text-neutral-500': !props.disabled,
              'text-neutral-400': props.disabled,
              'pl-1 pr-2': props.required,
              'px-1': !props.required,
              'after:text-subtitle-2 after:text-danger-600 after:content-["*"] after:absolute after:-translate-y-1.5': props.required,
              'peer-placeholder-shown:after:-translate-y-0': props.required && !hasPlaceholder.value,
              'peer-placeholder-shown:top-2 peer-placeholder-shown:px-0 peer-placeholder-shown:text-input': !hasPlaceholder.value,
              'peer-placeholder-shown:before:bg-transparent': !hasPlaceholder.value
            }
          )}
        >{props.label}
        </label>}
      {
        errorMessage.value && (
          <div
            t-data={`${tData.value}-error`}
            class='absolute -bottom-1 left-3 inline-block w-full translate-y-full text-input-label text-danger-600'
          >
            {errorMessage.value}
          </div>)
      }
      {
        isShowDescription.value && (
          <div
            t-data={`${tData.value}-description`}
            class='absolute -bottom-1 left-3 inline-block w-full translate-y-full text-input-label text-neutral-500'
          >
            {props.description}
          </div>)
      }
    </div>
  )
},
{
  name: 'VTextarea',
  props: [
    'mainRef',
    'tData',
    'class',
    'inputClass',
    'name',
    'placeholder',
    'value',
    'maxlength',
    'min',
    'max',
    'disabled',
    'autofocus',
    'error',
    'description',
    'autocomplete',
    'required',
    'readonly',
    'isSelecteMode',
    'inputId',
    'label',
    'variant',
    'onFocus',
    'onBlur',
    'onInput',
    'onKeypress',
    'onKeydown'
  ]
})
